import React from "react"
import { GenericTableColumn } from "@ui-components/Table/GenericTable"

export interface HealthCheckItem {
    id: string
    name: string
    description: string | null
    cpu: number | null
    ram: number | null
    storage: number | null
    processStatus: { active: number; total: number } | null
    mainStatus: string | null
    ports: string | null
    date: string | null
}

export type HealthCheckMetric = "cpu" | "ram" | "storage" | "process" | "port"

export type OpenHistory = (row: HealthCheckItem, metric: HealthCheckMetric) => void

const METRIC_LABEL: Record<HealthCheckMetric, string> = {
    cpu: "CPU",
    ram: "RAM",
    storage: "Storage",
    process: "Process status",
    port: "Port status",
}

export const metricLabel = (metric: HealthCheckMetric): string => METRIC_LABEL[metric]

// A null value has no history worth opening, so it keeps the plain "-".
const historyCell = (
    row: HealthCheckItem,
    metric: HealthCheckMetric,
    value: React.ReactNode,
    onOpenHistory: OpenHistory
) => {
    if (value === null || value === undefined) return "-"
    const label = `${METRIC_LABEL[metric]} history`
    return (
        <button
            type="button"
            title={label}
            aria-label={`${label} for ${row.name}`}
            onClick={(event) => {
                event.stopPropagation()
                onOpenHistory(row, metric)
            }}
            className="underline text-semantic-text-primary hover:text-semantic-text-secondary rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-semantic-border-primary"
        >
            {value}
        </button>
    )
}

export const getColumns = (onOpenHistory: OpenHistory): GenericTableColumn<HealthCheckItem>[] => [
    {
        header: "Instance",
        accessor: "name",
        renderCell: (row) => row.name,
    },
    {
        header: "Status",
        accessor: "mainStatus",
        renderCell: (row) => row.mainStatus,
    },
    {
        header: "CPU (%)",
        accessor: "cpu",
        renderCell: (row) => historyCell(row, "cpu", row.cpu, onOpenHistory),
    },
    {
        header: "RAM (%)",
        accessor: "ram",
        renderCell: (row) => historyCell(row, "ram", row.ram, onOpenHistory),
    },
    {
        header: "Storage (%)",
        accessor: "storage",
        renderCell: (row) => historyCell(row, "storage", row.storage, onOpenHistory),
    },
    {
        header: "Process Status",
        accessor: "processStatus",
        renderCell: (row) =>
            historyCell(
                row,
                "process",
                row.processStatus ? `${row.processStatus.active}/${row.processStatus.total}` : null,
                onOpenHistory
            ),
    },
    {
        header: "Port Status",
        accessor: "ports",
        renderCell: (row) => historyCell(row, "port", row.ports, onOpenHistory),
    },
    {
        header: "Last Update",
        accessor: "date",
        renderCell: (row) =>
            row.date ? new Date(row.date).toLocaleString() : "-",
    },
]
